/**
 * Application configuration.
 *
 * NOTE: In a real production environment, these should come from environment variables
 * (e.g., via webpack DefinePlugin or similar).
 */
const API_URLS = {
    production: "https://api.putty.me",
    "staging-1": "https://staging-api-1.putty.zone",
    "staging-2": "https://staging-api-2.putty.zone",
    "staging-3": "https://staging-api-3.putty.zone",
    "staging-khc": "https://staging-api-khc.putty.zone",
    "staging-hsp": "https://staging-api-hsp.putty.zone",
    "staging-mm": "https://staging-api-mm.putty.zone",
    "staging-sg": "https://staging-api-sg.putty.zone",
    local: "http://localhost:8787",
};
function isApiTarget(value) {
    return (value === "production" ||
        value === "staging-1" ||
        value === "staging-2" ||
        value === "staging-3" ||
        value === "staging-khc" ||
        value === "staging-hsp" ||
        value === "staging-mm" ||
        value === "staging-sg" ||
        value === "local");
}
/**
 * Fallback API target for `yarn dev` when no per-worktree override is set in
 * the gitignored local config file.
 *
 * The actual `yarn dev` target is resolved as:
 *
 *     process.env.DEV_API_TARGET (from .dev-local.json) ?? DEFAULT_LOCAL_DEV_API_TARGET
 *
 * To pin a per-worktree target without touching this constant, set `apiTarget`
 * in `production/web-client/viewer/.dev-local.json` (gitignored — never
 * committed). Easiest path:
 *
 *     "Putty: Set Viewer API Target"   (worktree-creator VS Code command)
 *
 * or edit the JSON directly:
 *
 *     { "apiTarget": "staging-khc" }
 *
 * Deployed viewer builds use NODE_ENV=production (see deploy-viewer.yml),
 * which always targets the production API — neither the local file nor this
 * fallback is consulted in production builds.
 */
const DEFAULT_LOCAL_DEV_API_TARGET = "staging-1";
/**
 * Resolves the per-worktree override from `.dev-local.json` (injected via
 * webpack DefinePlugin as `process.env.DEV_API_TARGET`). Returns `undefined`
 * when the value is missing or not a recognised `ApiTarget` so the `??`
 * chain below falls through to the default.
 */
function readLocalDevApiTarget() {
    const raw = process.env.DEV_API_TARGET;
    return typeof raw === "string" && isApiTarget(raw) ? raw : undefined;
}
const LOCAL_DEV_API_TARGET = readLocalDevApiTarget() ?? DEFAULT_LOCAL_DEV_API_TARGET;
const apiTarget = process.env.NODE_ENV === "production" ? "production" : LOCAL_DEV_API_TARGET;
export const viewerConfig = {
    apiTarget: apiTarget,
    version: "viewer_0.1.26",
    /*
      Version history:
      0.1.26: Fix the mobile toolbar layout
      0.1.25: Hide the toolbar on the Memory cards
      0.1.24: Let people load all memories
      0.1.23: Add lasso selection
      0.1.22: Add reminders + bump
      0.1.21: Fix search results. Add in favicon.
      0.1.20: Ensure pinned memories are always loaded
      0.1.19: Add pinning to memory cards
      0.1.18: Fix link card border color, fix styling
      0.1.17: Fix mobile tap-to-open + haptic on long hold
      0.1.16: Fix opening sidebar on mobile
      0.1.15: Add support for month timeline scrubber
      0.1.14: Add audio player support
      0.1.13: Add support for timeline sidebar
      0.1.12: Test Sentry in viewer
      0.1.11: Add in toasts
      0.1.9: Add support for long press drag
      0.1.8: Migrate to use tan-stack-db
      0.1.7: Add WIP region with random memories
      0.1.6: Add haptic feedback test
      0.1.5: Add support for mobile device
    */
    supabase: {
        url: "https://base.putty.me",
        anonKey: "sb_publishable_DHCHZKaF3gKLs6o5uZ1Lbw_0_yTl45L",
    },
};
/** Resolved API base URL. All API calls should use this. */
export const API_BASE_URL = API_URLS[viewerConfig.apiTarget];
/** Returns the staging slot when the viewer targets a staging API; otherwise
 *  `undefined`. Consumed by the staging chip in the slate header so the
 *  client always advertises *which* staging slot it's hitting. */
export function getStagingSlot() {
    const target = viewerConfig.apiTarget;
    if (target === "staging-1" ||
        target === "staging-2" ||
        target === "staging-3" ||
        target === "staging-khc" ||
        target === "staging-hsp" ||
        target === "staging-mm" ||
        target === "staging-sg") {
        return target;
    }
    return undefined;
}
/** True when the viewer dev server runs on this machine (e.g. yarn dev → localhost). */
export function isViewerDevLocalhost() {
    if (typeof window === "undefined")
        return false;
    if (process.env.NODE_ENV !== "development")
        return false;
    const host = window.location.hostname;
    return host === "localhost" || host === "127.0.0.1";
}
/**
 * True when the bundle was produced by a dev build (webpack.dev.cjs), regardless
 * of how the page was loaded — direct on `localhost:3000`, through a tunnel
 * (ngrok / Cloudflare Tunnel / localtunnel), or via a LAN IP for phone testing.
 *
 * Use this for behaviour that is tied to "this is a development build talking
 * to a real backend" rather than "this dev server is running on my machine".
 * The Cloudflare Access gate is the canonical case: whether the page is served
 * from `localhost` or `abc123.ngrok-free.app`, every API call still goes to
 * the same staging hostname and will still hit Access — the gate has to fire
 * the same way regardless.
 *
 * Prefer `isViewerDevLocalhost()` when you specifically need "this developer
 * is on their own machine right now" (e.g. surfacing a debug-only UI button
 * that shouldn't appear on a phone someone else is testing through your
 * tunnel).
 */
export function isViewerDevBuild() {
    return process.env.NODE_ENV === "development";
}
/** @deprecated Use API_BASE_URL instead */
export const DEV_API_URL = API_BASE_URL;
